import { useQuery } from '@apollo/client';
import { oneLine } from 'common-tags';
import { loader } from 'graphql.macro';
import moment from 'moment-timezone';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components/macro';

import { hideServiceModal } from 'actions';
import { CalendarEventDownloadButton } from 'components/CalendarEventDownloadButton';
import { Phrase } from 'components/Phrase';
import { useStringContext } from 'components/StringContext';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import {
  CalendarService,
  DateTimeFormat,
  IBuildThankYouRedirectUrlOptions,
  IState,
} from 'typings';
import { IServiceCodeQuery, IServiceCodeQueryVariables } from 'typings/GraphQL';
import { buildThankYouRedirectUrl } from 'utils/buildThankYouRedirectUrl';
import { formatPartnerAddress } from 'utils/formatPartnerAddress';

import {
  Container,
  Description,
  EnterpriseDescription,
  EnterpriseDetails,
  Header,
  Logo,
  PreferredPartner,
  PreferredPartnerAddress,
  PreferredPartnerLabel,
  PreferredPartnerName,
  SummaryDetails,
  SummaryImage,
  SummaryItem,
  SummaryItems,
} from './styles';

const serviceCodeQuery = loader('../../queries/serviceCode.graphql');

export const CarouselItemConfirmFinishRepairTop = () => {
  const theme = useTheme();
  const dispatch = useThunkDispatch();
  const strings = useStringContext();
  const {
    address: {
      city = '',
      state: addressState = '',
      street1 = '',
      street2 = '',
      timezone = '',
      zipCode = '',
    },
    appointmentId,
    clientKey,
    leadId,
    preferredPartner,
    selectedTimeSlot,
    serviceCodeId,
    sourceTrackingAnalytics,
    thankYouRedirectUrl,
  } = useSelector((state: IState) => ({
    address: state.userInput.address,
    appointmentId: state.userInput.appointmentId,
    clientKey: state.client.key,
    leadId: state.userInput.leadId,
    preferredPartner: state.userInput.preferredPartner,
    selectedTimeSlot: state.userInput.selectedTimeSlot,
    serviceCodeId: state.userInput.serviceCodeId,
    sourceTrackingAnalytics: state.sourceTrackingAnalytics,
    thankYouRedirectUrl: state.serviceCarousel.thankYouRedirectUrl,
  }));

  const { endTime = '', label = null, startTime = '' } = selectedTimeSlot || {};

  const { data: serviceCodeData } = useQuery<
    IServiceCodeQuery,
    IServiceCodeQueryVariables
  >(serviceCodeQuery, {
    skip: !serviceCodeId,
    variables: {
      id: serviceCodeId || '',
    },
  });

  const fileNameDateString = moment
    .tz(startTime, timezone)
    .format(DateTimeFormat.DATE_ICS_FILE_NAME);
  const event = !appointmentId
    ? null
    : {
        appointmentId,
        description: strings['CONFIRM_FINISH_REPAIR.EVENT.DESCRIPTION'] ?? '',
        endTime,
        fileName: `${clientKey}-${fileNameDateString}`,
        location: oneLine`
            ${street1},
            ${street2 ? `${street2},` : ''}
            ${city}, ${addressState} ${zipCode}
        `,
        startTime,
        title: strings['CONFIRM_FINISH_REPAIR.EVENT.TITLE'] ?? '',
      };

  useEffect(() => {
    if ((appointmentId || leadId) && thankYouRedirectUrl) {
      const params = new URL(window.location.href).searchParams;
      const clientUrl = params.get('client_url') || '';

      const options: IBuildThankYouRedirectUrlOptions = {
        dateCreated: new Date().toJSON(),
        endTime,
        serviceCodeKey: serviceCodeData?.serviceCode?.key,
        serviceCodeName: serviceCodeData?.serviceCode?.name,
        startTime,
        tradeName: serviceCodeData?.serviceCode?.trade?.name,
        ...sourceTrackingAnalytics,
      };

      const redirectUrl = buildThankYouRedirectUrl(
        clientUrl,
        thankYouRedirectUrl,
        options,
      );

      if (redirectUrl) {
        dispatch(hideServiceModal());
        window.open(redirectUrl, '_top');
      }
    }
  }, [
    appointmentId,
    endTime,
    dispatch,
    leadId,
    serviceCodeData,
    sourceTrackingAnalytics,
    startTime,
    timezone,
    thankYouRedirectUrl,
  ]);

  return (
    <>
      <Header>
        <Phrase id="CONFIRM_FINISH_REPAIR.HEADER" />
      </Header>
      <Container>
        {theme.enterpriseEnabled && theme.enterpriseVersion === '2' ? (
          <EnterpriseDetails>
            <EnterpriseDescription>
              <Phrase id="CONFIRM_FINISH_REPAIR.DESCRIPTION" />
            </EnterpriseDescription>
            {preferredPartner && (
              <>
                <PreferredPartnerLabel>
                  <Phrase id="CONFIRM_FINISH_REPAIR.PARTNER.LABEL" />
                </PreferredPartnerLabel>
                <PreferredPartner>
                  <PreferredPartnerName>
                    {preferredPartner.name}
                  </PreferredPartnerName>
                  <PreferredPartnerAddress>
                    {formatPartnerAddress(preferredPartner.address).join(', ')}
                  </PreferredPartnerAddress>
                </PreferredPartner>
              </>
            )}
          </EnterpriseDetails>
        ) : (
          <SummaryDetails>
            <div>
              <Logo src={theme.logoReverseUrl} />
            </div>
            <SummaryItems>
              <Description>
                <Phrase id="CONFIRM_FINISH_REPAIR.DESCRIPTION" />
              </Description>
              {!label && event && (
                <div>
                  <SummaryItem>
                    <SummaryImage />
                    <span>
                      <Phrase id="CONFIRM_FINISH_REPAIR.CALENDAR.LABEL" />
                    </span>
                  </SummaryItem>
                  <CalendarEventDownloadButton
                    calendarService={CalendarService.GOOGLE}
                    event={event}
                  />
                  <CalendarEventDownloadButton
                    calendarService={CalendarService.APPLE}
                    event={event}
                  />
                  <CalendarEventDownloadButton
                    calendarService={CalendarService.OUTLOOK}
                    event={event}
                  />
                </div>
              )}
            </SummaryItems>
          </SummaryDetails>
        )}
      </Container>
    </>
  );
};
